import _ from 'lodash';
import * as mobx from 'mobx';
import { CoreFetch, DocumentApi, MessageApi, MessageLinkEntityType, ProviderClaimDetails, ProviderComment, ProviderMessage } from '@enter/core';
import { handleError, IMessageDetail, RootStore } from '../../stores';

export class MessagingPresenter {
    @mobx.observable.ref isOpen: boolean = false;
    @mobx.observable.ref isComposeOpen: boolean = false;
    @mobx.observable.ref isDetailLoading: boolean = false;
    @mobx.observable.ref isDocumentsLoading: boolean = false;
    @mobx.observable.ref documentsLoadError: string | null = null;
    @mobx.observable.ref messageDetails: IMessageDetail | undefined;

    constructor(private rootStore = new RootStore()) {
        mobx.makeObservable(this);
    }

    @mobx.action
    public setOpen(isOpen: boolean) {
        this.isOpen = isOpen;

        if (isOpen === false) {
            this.closeDetails();
        }
    }

    @mobx.action
    public setComposeOpen(isComposeOpen: boolean) {
        this.isComposeOpen = isComposeOpen;

        if (isComposeOpen === false) {
            this.closeDetails();
        }
    }

    @mobx.action
    public async openMessage(message: ProviderMessage, claimDetails?: ProviderClaimDetails) {
        const messageId = message.id!;
        
        this.isDetailLoading = true;
        this.isDocumentsLoading = false;
        this.documentsLoadError = null;

        try {
            let comments: ProviderComment[] | undefined;

            // Try to get comments from the message object first
            if (Array.isArray(message.comments) && message.comments.length > 0) {
                comments = _.orderBy(message.comments, 'created', 'asc');
            } 
            // Try to get comments from claimDetails
            else if (claimDetails) {
                const claimDetail = await this.loadClaimDetailsMessageDetails(messageId, claimDetails);
                if (claimDetail?.comments) {
                    comments = claimDetail.comments;
                }
            }

            // If we have comments, show the message immediately
            if (comments && comments.length > 0) {
                const detail: IMessageDetail = {
                    message,
                    comments,
                    documents: [], // Documents will be loaded asynchronously
                };
                
                mobx.runInAction(() => {
                    this.messageDetails = detail;
                    this.isDetailLoading = false;
                });
                
                // Fetch documents asynchronously
                this.fetchDocumentsAsync(messageId, comments);
                return;
            }

            // If no comments available, we need to fetch from API
            const detail = await this.rootStore.messageStore.loadMessageDetail(messageId);
            if (!detail) {
                return;
            }

            mobx.runInAction(() => {
                this.messageDetails = detail;
                this.isDetailLoading = false;
            });
        } catch (err) {
            handleError(err);
            mobx.runInAction(() => {
                this.isDetailLoading = false;
            });
        }
    }

    /**
     * Open message by ID - always fetches fresh data from API
     * Use this when you don't have the full message object or want fresh data
     */
    @mobx.action
    public async openMessageById(messageId: string) {
        this.isDetailLoading = true;
        this.isDocumentsLoading = false;
        this.documentsLoadError = null;

        try {
            // Always fetch fresh data from API
            const detail = await this.rootStore.messageStore.loadMessageDetail(messageId);
            if (!detail) {
                return;
            }

            mobx.runInAction(() => {
                this.messageDetails = detail;
                this.isDetailLoading = false;
            });
        } catch (err) {
            handleError(err);
            mobx.runInAction(() => {
                this.isDetailLoading = false;
            });
        }
    }
    
    /**
     * Fetch documents asynchronously and update messageDetails when done.
     * Tracks loading state and errors so UI can display appropriate feedback.
     */
    private async fetchDocumentsAsync(messageId: string, comments: ProviderComment[]): Promise<void> {
        const documentIds = _.flatMap(comments, 'documents').filter(Boolean) as string[];
        if (documentIds.length === 0) {
            return;
        }

        mobx.runInAction(() => {
            this.isDocumentsLoading = true;
            this.documentsLoadError = null;
        });

        try {
            const filterId = documentIds.map(id => `EQ:${id}`);
            const documents = await CoreFetch(DocumentApi).listProviderDocuments({ filterId });
            
            // Only update if we're still viewing the same message
            mobx.runInAction(() => {
                if (this.messageDetails?.message?.id === messageId) {
                    this.messageDetails = {
                        ...this.messageDetails,
                        documents: documents || [],
                    };
                }
                this.isDocumentsLoading = false;
            });
        } catch (err) {
            console.error('[MessagingPresenter.fetchDocumentsAsync] error', err);
            mobx.runInAction(() => {
                this.isDocumentsLoading = false;
                // Only set error if we're still viewing the same message
                if (this.messageDetails?.message?.id === messageId) {
                    this.documentsLoadError = 'Failed to load attachments';
                }
            });
        }
    }

    private async loadClaimDetailsMessageDetails(messageId: string, claimDetails: ProviderClaimDetails): Promise<IMessageDetail | undefined> {
        // Check if message exists in claimDetails.messages
        const hasMessages = Array.isArray(claimDetails?.messages) && claimDetails.messages.length > 0;
        
        if (hasMessages) {
            const matchedMessage = claimDetails.messages!.find(m => m.id === messageId);
            
            if (matchedMessage) {
                let comments: ProviderComment[] | undefined;
                
                // Check if message has comments array (from new API)
                if (Array.isArray(matchedMessage.comments) && matchedMessage.comments.length > 0) {
                    comments = matchedMessage.comments;
                } else if (Array.isArray(claimDetails.comments) && claimDetails.comments.length > 0) {
                    // Fallback: filter comments from claimDetails by messageId
                    comments = claimDetails.comments.filter(c => c.messageId === messageId);
                }
                
                // If we have comments from claimDetails, use the message directly
                if (comments && comments.length > 0) {
                    // Order comments from oldest to newest
                    const orderedComments = _.orderBy(comments, 'created', 'asc');
                    
                    return {
                        message: matchedMessage,
                        comments: orderedComments,
                        documents: undefined, // Documents can be loaded separately if needed
                    };
                }
            }
        }
        
        return undefined;
    }

    @mobx.action
    public async linkEntityToMessage(id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> {
        try {
            // Call API to link entity first
            await CoreFetch(MessageApi).postProviderMessageLink({
                id,
                providerMessageLinkPayload: { entityType, entityIds }
            });
            
            // Reload message details from store to get updated links
            const detail = await this.rootStore.messageStore.loadMessageDetail(id);
            
            // Update presenter with fresh data
            mobx.runInAction(() => {
                this.messageDetails = detail;
            });
        } catch (error) {
            console.error('Failed to link entity:', error);
            throw error;
        }
    }

    @mobx.action
    public async unlinkEntityFromMessage(id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> {
        try {
            // Call API to unlink entity first
            await CoreFetch(MessageApi).postProviderMessageUnlink({
                id,
                providerMessageLinkPayload: { entityType, entityIds }
            });
            
            // Reload message details from store to get updated links
            const detail = await this.rootStore.messageStore.loadMessageDetail(id);
            
            // Update presenter with fresh data
            mobx.runInAction(() => {
                this.messageDetails = detail;
            });
        } catch (error) {
            console.error('Failed to unlink entity:', error);
            throw error;
        }
    }

    @mobx.action
    public closeDetails() {
        this.messageDetails = undefined;
        this.isDocumentsLoading = false;
        this.documentsLoadError = null;
    }
}
